import type { BoxProps } from "@mui/material/Box"

import { useId, forwardRef } from "react"
import { useAuthContext } from "@/src/auth/hooks"

import Box from "@mui/material/Box"
import NoSsr from "@mui/material/NoSsr"
import { useTheme } from "@mui/material/styles"
import { Stack, Typography } from "@mui/material"
import { deepOrange } from "@mui/material/colors"

import { RouterLink } from "src/routes/components"

import { logoClasses } from "./classes"

// ----------------------------------------------------------------------

export type LogoProps = BoxProps & {
    href?: string
    disableLink?: boolean
    disableBg?: boolean
}

export const Logo = forwardRef<HTMLDivElement, LogoProps>(
    ({ width = 39, height = 39, disableLink = false, disableBg = false, className, href = "/", sx, ...other }, ref) => {
        const { user } = useAuthContext()
        const theme = useTheme()

        const gradientId = useId()

        const PRIMARY_LIGHT = theme.vars.palette.primary.light

        const PRIMARY_MAIN = theme.vars.palette.primary.main

        const PRIMARY_DARK = theme.vars.palette.primary.dark

        const logo = user?.company?.logoUrl ? (
            <Box
                alt="logo"
                component="img"
                src={user.company.logoUrl ?? undefined}
                width={width}
                height={height}
                sx={{ objectFit: "contain" }}
            />
        ) : (
            <Stack
                sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: user?.company?.logoUrl || disableBg ? undefined : deepOrange[500],
                    width,
                    height,
                }}
            >
                <Typography variant="h6" color="white">
                    {user?.company?.name?.charAt(0)}
                </Typography>
            </Stack>
        )

        return (
            <NoSsr
                fallback={
                    <Box
                        width={width}
                        height={height}
                        className={logoClasses.root.concat(className ? ` ${className}` : "")}
                        sx={{ flexShrink: 0, display: "inline-flex", verticalAlign: "middle", ...sx }}
                    />
                }
            >
                <Box
                    ref={ref}
                    component={RouterLink}
                    href={href}
                    width={width}
                    height={height}
                    className={logoClasses.root.concat(className ? ` ${className}` : "")}
                    aria-label="logo"
                    sx={{
                        textDecoration: "none",
                        flexShrink: 0,
                        display: "inline-flex",
                        verticalAlign: "middle",
                        ...(disableLink && { pointerEvents: "none" }),
                        ...sx,
                    }}
                    {...other}
                >
                    {logo}
                </Box>
            </NoSsr>
        )
    }
)
